/**
 * Coordinates local and remote run cancellation, and reloads conversation
 * history when a remote run finishes.
 *
 * Must be called inside `AssistantRuntimeProvider` (uses `useAuiState`).
 */
import { useCallback, useEffect, useState } from "react";
import type { HttpAgent } from "@ag-ui/client";
import type { AssistantRuntime } from "@assistant-ui/react";
import { useAuiState } from "@assistant-ui/react";
import type { AssistantApi } from "~/components/assistant/api/AssistantApi";
import { fetchConversationHistory } from "./useConversationHistory";
import { useConversationStatus } from "./useConversationStatus";

interface UseRunCancellationOptions {
  api: AssistantApi;
  agent: HttpAgent;
  runtime: AssistantRuntime;
  conversationId: string | null;
  clearRunError: () => void;
}

interface UseRunCancellationReturn {
  isRunningRemotely: boolean;
  onCancelRemoteRun: () => Promise<void>;
  onCancelLocalRun: () => Promise<void>;
  isCancelling: boolean;
}

export function useRunCancellation({
  api,
  agent,
  runtime,
  conversationId,
  clearRunError,
}: UseRunCancellationOptions): UseRunCancellationReturn {
  const isLocalRunActive = useAuiState((s) => s.thread.isRunning);
  const [isCancelling, setIsCancelling] = useState(false);

  const { isRunningRemotely, remoteRunFinishedCount, refetchStatus } = useConversationStatus({
    api,
    conversationId,
    isLocalRunActive,
  });

  // When a remote run finishes, reload conversation history and clear cancel state.
  useEffect(() => {
    if (remoteRunFinishedCount === 0 || !conversationId) return;

    setIsCancelling(false);

    (async () => {
      try {
        const messages = await fetchConversationHistory(conversationId);
        if (messages.length > 0) {
          runtime.thread.reset(messages);
        }
      } catch (error) {
        console.error("Failed to reload history after remote run finished:", error);
      }
    })();
  }, [remoteRunFinishedCount, conversationId, runtime]);

  // Clear cancel state on conversation change
  useEffect(() => {
    setIsCancelling(false);
  }, [conversationId]);

  const onCancelLocalRun = useCallback(async () => {
    if (!conversationId) return;
    clearRunError();
    setIsCancelling(true);
    // Abort the SSE fetch so the stream stops immediately.
    agent.abortRun();
    try {
      await api.cancelMessage(conversationId);
    } catch (error) {
      console.error("Failed to notify backend of cancellation:", error);
    }
    // Re-fetch status so the poll kicks in and waits for the backend to finish.
    // If the backend already finished before we refetch, clear cancel state now.
    const convo = await refetchStatus();
    if (convo?.status == null || convo.status === "READY") {
      setIsCancelling(false);
    }
  }, [api, agent, conversationId, clearRunError, refetchStatus]);

  const onCancelRemoteRun = useCallback(async () => {
    if (!conversationId) return;
    setIsCancelling(true);
    try {
      await api.cancelMessage(conversationId);
    } catch (error) {
      console.error("Failed to cancel remote run:", error);
      setIsCancelling(false);
    }
  }, [api, conversationId]);

  return { isRunningRemotely, onCancelRemoteRun, onCancelLocalRun, isCancelling };
}
